import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { ClipboardCheck, Printer, Calculator, Save, Search, Upload, History as HistoryIcon, MessageSquare, Check, UserCog, Filter, Smartphone, Grid3x3 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import WalkTheRacksCounter from './WalkTheRacksCounter';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ADMIN_ROLES = new Set(['admin', 'auto_spares_admin', 'auto_spares_super']);

export default function StockTakePanel() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [counts, setCounts] = useState({}); // {product_id: counted_qty}
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printMode, setPrintMode] = useState('with_qty'); // 'with_qty' | 'blind'
  const [hideSystemQty, setHideSystemQty] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState('');
  const [noteDrafts, setNoteDrafts] = useState({}); // {product_id: editing text}
  const [savingNote, setSavingNote] = useState({}); // {product_id: bool}
  const [showClerkOnly, setShowClerkOnly] = useState(false);
  const [applyingCaptured, setApplyingCaptured] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('stock_take_view') || 'grid'); // 'grid' | 'walk'

  useEffect(() => { localStorage.setItem('stock_take_view', viewMode); }, [viewMode]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [priceMap, setPriceMap] = useState({}); // product_id -> { last_sold_price, source, value }

  const fetchData = useCallback(async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/api/auto-spares/products`, { headers }),
        axios.get(`${API_URL}/api/stock-take/categories`, { headers }),
      ];
      if (isAdmin) {
        requests.push(axios.get(`${API_URL}/api/auto-spares/products/future-value`, { headers }));
      }
      const results = await Promise.all(requests);
      const [pRes, cRes, fvRes] = results;
      const prods = pRes.data || [];
      setProducts(prods);
      setCategories(cRes.data?.categories || []);
      if (isAdmin && fvRes?.data?.breakdown) {
        const m = {};
        fvRes.data.breakdown.forEach(b => { m[b.product_id] = b; });
        setPriceMap(m);
      }
      const persisted = {};
      prods.forEach(p => {
        if (p.counted_qty !== undefined && p.counted_qty !== null) {
          persisted[p.id] = String(p.counted_qty);
        }
      });
      setCounts(persisted);
    } catch {
      toast.error('Failed to load products');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const q = (searchText || '').trim().toLowerCase();
    return products.filter(p => {
      if (selectedCategory !== 'all' && p.category !== selectedCategory) return false;
      if (q) {
        const blob = `${p.name || ''} ${p.category || ''} ${p.vehicle_source || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (showClerkOnly) {
        // Only rows a clerk has touched (count OR note)
        const clerkCount = p.counted_by_role === 'clerk';
        const clerkNote = !!p.stock_notes && p.stock_notes_by && p.stock_notes_by !== (user?.full_name || user?.username);
        if (!clerkCount && !clerkNote) return false;
      }
      return true;
    });
  }, [products, selectedCategory, searchText, showClerkOnly, user]);

  // Count of clerk-captured rows in the full dataset (used for the button badge)
  const clerkCapturedCount = useMemo(
    () => products.filter(p => p.counted_by_role === 'clerk').length,
    [products]
  );

  // Overall progress across the entire catalog (used for header progress bar)
  const overallProgress = useMemo(() => {
    const total = products.length;
    const counted = products.filter(p => p.counted_qty !== undefined && p.counted_qty !== null).length;
    const pct = total ? Math.round((counted / total) * 100) : 0;
    return { total, counted, pct };
  }, [products]);

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const params = { mode: printMode };
      if (selectedCategory !== 'all') params.category = selectedCategory;
      const res = await axios.get(`${API_URL}/api/stock-take/sheet-pdf`, {
        headers, params, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const modeTag = printMode === 'blind' ? '_blind' : '';
      a.download = `stock_take${modeTag}_${selectedCategory}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      // Also open for printing
      const w = window.open(url);
      if (w) setTimeout(() => w.print(), 600);
      // For blind mode, automatically hide System Qty in the on-screen capture table
      if (printMode === 'blind') setHideSystemQty(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Print failed');
    } finally {
      setPrinting(false);
    }
  };

  const setCount = (productId, value) => {
    setCounts(prev => ({ ...prev, [productId]: value }));
    setPreview(null); // clear preview if counts change
  };

  // Persist the count on the product so admins can see clerk-captured
  // values and the count survives navigation/session.
  const persistCount = async (product, rawValue) => {
    const persisted = product.counted_qty;
    let payload;
    if (rawValue === '' || rawValue === undefined || rawValue === null) {
      // empty => clear
      if (persisted === undefined || persisted === null) return; // nothing to clear
      payload = { counted_qty: null };
    } else {
      const n = parseInt(rawValue);
      if (isNaN(n) || n < 0) return;
      if (persisted !== undefined && persisted !== null && parseInt(persisted) === n) return; // unchanged
      payload = { counted_qty: n };
    }
    try {
      const res = await axios.put(
        `${API_URL}/api/stock-take/count/${product.id}`,
        payload,
        { headers }
      );
      setProducts(prev => prev.map(pp => pp.id === product.id ? {
        ...pp,
        counted_qty: res.data.counted_qty ?? undefined,
        counted_by: res.data.counted_by,
        counted_by_role: res.data.counted_by_role,
        counted_at: res.data.counted_at,
      } : pp));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save count');
    }
  };

  const handleApplyCaptured = async () => {
    if (clerkCapturedCount === 0) return;
    if (!window.confirm(
      `Apply all ${clerkCapturedCount} clerk-captured counts?\n\n` +
      `• product.quantity will be overwritten with the counted quantity\n` +
      `• variance for these products becomes zero\n` +
      `• each product's note is kept as history\n` +
      `• a stock take log entry is written\n\n` +
      `Cannot be undone.`
    )) return;
    setApplyingCaptured(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/stock-take/apply-captured`,
        { source: 'clerks_only', notes: `Admin accepted ${clerkCapturedCount} clerk counts` },
        { headers }
      );
      toast.success(`Applied ${res.data.rows_updated} clerk-captured counts`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply captured counts');
    } finally {
      setApplyingCaptured(false);
    }
  };

  const collectedCounts = useMemo(() => {
    return Object.entries(counts)
      .filter(([, v]) => v !== '' && v !== undefined && v !== null)
      .map(([product_id, counted_qty]) => ({ product_id, counted_qty: parseInt(counted_qty) || 0 }));
  }, [counts]);

  const handlePreview = async () => {
    if (collectedCounts.length === 0) {
      toast.error('Enter at least one counted quantity');
      return;
    }
    setPreviewing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/stock-take/preview`,
        { counts: collectedCounts },
        { headers }
      );
      setPreview(res.data);
      toast.success(`Preview ready: ${res.data.summary.matches} match, ${res.data.summary.shortages} short, ${res.data.summary.overages} over`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Apply this stock take?\n\n` +
      `• ${preview.summary.matches} matched (no change)\n` +
      `• ${preview.summary.shortages} shortages (qty will go down)\n` +
      `• ${preview.summary.overages} overages (qty will go up)\n\n` +
      `Net value impact: ${R(preview.summary.overage_value - preview.summary.shortage_value)}\n\n` +
      'This will overwrite current product quantities. Cannot be undone.'
    )) return;

    setApplying(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/stock-take/apply`,
        { counts: collectedCounts, notes },
        { headers }
      );
      toast.success(`Applied — ${res.data.rows_updated} products updated`);
      setCounts({});
      setPreview(null);
      setNotes('');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleFillFromSystem = () => {
    if (!window.confirm('Pre-fill all counted quantities with system quantities? Useful for spot-checks where most counts match.')) return;
    const next = {};
    filtered.forEach(p => { next[p.id] = p.quantity; });
    setCounts(next);
    toast.info(`Pre-filled ${filtered.length} rows`);
  };

  const handleClear = () => {
    setCounts({});
    setPreview(null);
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stock-take/log`, { headers });
      setHistory(res.data || []);
      setHistoryOpen(true);
    } catch {
      toast.error('Failed to load history');
    }
  };

  const noteDraftValue = (p) => {
    // draft (currently being edited) OR the saved value
    const d = noteDrafts[p.id];
    if (d !== undefined) return d;
    return p.stock_notes || '';
  };

  const noteChanged = (p) => {
    const d = noteDrafts[p.id];
    if (d === undefined) return false;
    return d !== (p.stock_notes || '');
  };

  const handleNoteChange = (productId, value) => {
    setNoteDrafts(prev => ({ ...prev, [productId]: value }));
  };

  const handleNoteSave = async (product) => {
    const draft = noteDrafts[product.id];
    if (draft === undefined || draft === (product.stock_notes || '')) return;
    setSavingNote(prev => ({ ...prev, [product.id]: true }));
    try {
      const res = await axios.put(
        `${API_URL}/api/stock-take/notes/${product.id}`,
        { stock_notes: draft },
        { headers }
      );
      // Update local products state with the new note metadata
      setProducts(prev => prev.map(pp => pp.id === product.id ? {
        ...pp,
        stock_notes: res.data.stock_notes,
        stock_notes_by: res.data.stock_notes_by,
        stock_notes_at: res.data.stock_notes_at,
      } : pp));
      setNoteDrafts(prev => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
      toast.success('Note saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingNote(prev => ({ ...prev, [product.id]: false }));
    }
  };

  return (
    <div className="space-y-5">
      {/* View mode toggle - Grid (desktop, current) vs Walk the Racks (mobile) */}
      <div className="flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-lg p-1 w-fit">
        <button
          onClick={() => setViewMode('grid')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition ${viewMode === 'grid' ? 'bg-cyan-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
          data-testid="stock-take-view-grid"
        >
          <Grid3x3 className="h-4 w-4" /> Grid View
        </button>
        <button
          onClick={() => setViewMode('walk')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition ${viewMode === 'walk' ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
          data-testid="stock-take-view-walk"
        >
          <Smartphone className="h-4 w-4" /> Walk the Racks
        </button>
      </div>

      {viewMode === 'walk' && <WalkTheRacksCounter />}

      {viewMode === 'grid' && <>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-cyan-400" /> Stock Take Sheets
          </h2>
          <p className="text-slate-400 text-sm">Print physical count sheets, capture counts, reconcile variances, apply.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs text-slate-400 mb-1 block">Sheet mode</Label>
            <Select value={printMode} onValueChange={setPrintMode}>
              <SelectTrigger className="w-44 bg-slate-900 border-slate-700 text-white" data-testid="stock-take-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="with_qty">With System Qty</SelectItem>
                <SelectItem value="blind">Blind Count (no qty)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={fetchHistory} variant="outline"
            className="bg-slate-900 border-slate-700 text-white hover:bg-slate-700"
            data-testid="stock-take-history-btn">
            <HistoryIcon className="h-4 w-4 mr-1" /> History
          </Button>
          {isAdmin && (
            <Button onClick={handleApplyCaptured}
              disabled={applyingCaptured || clerkCapturedCount === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
              data-testid="apply-clerk-captured-btn"
              title="Reset variance to counted stock for all products captured by clerks">
              <UserCog className="h-4 w-4 mr-1" />
              {applyingCaptured ? 'Applying...' : `Apply Clerk Counts${clerkCapturedCount > 0 ? ` (${clerkCapturedCount})` : ''}`}
            </Button>
          )}
          <Button onClick={handlePrint} disabled={printing}
            className={printMode === 'blind' ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-cyan-600 hover:bg-cyan-700 text-white"}
            data-testid="print-stock-sheet-btn">
            <Printer className="h-4 w-4 mr-1" /> {printing ? 'Generating...' : (printMode === 'blind' ? 'Print Blind Sheet' : 'Print Sheet')}
          </Button>
        </div>
      </div>

      {/* Overall progress bar — resumable running stock take */}
      <Card className="bg-slate-800/60 border-slate-700" data-testid="stock-take-progress-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-slate-300">
              <span className="font-semibold text-white" data-testid="stock-take-progress-label">
                {overallProgress.counted} of {overallProgress.total} counted
              </span>
              <span className="text-slate-400 text-xs ml-2">
                ({overallProgress.pct}%) · {Math.max(overallProgress.total - overallProgress.counted, 0)} still to count
              </span>
            </div>
            <span className={`text-xs font-semibold ${overallProgress.pct === 100 ? 'text-emerald-400' : 'text-cyan-300'}`}>
              {overallProgress.pct === 100 ? 'All items captured' : 'In progress'}
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-900 border border-slate-700 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${overallProgress.pct === 100 ? 'bg-emerald-500' : 'bg-cyan-500'}`}
              style={{ width: `${overallProgress.pct}%` }}
              data-testid="stock-take-progress-bar"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Counts persist server-side — you can pause anytime and resume tomorrow. Clerk-captured rows are highlighted below.
          </p>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-slate-400">Search</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
              <Input value={searchText} onChange={(e) => setSearchText(e.target.value)}
                placeholder="Product name, category, vehicle..."
                className="pl-9 bg-slate-900 border-slate-700 text-white"
                data-testid="stock-take-search" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Category</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-56 bg-slate-900 border-slate-700 text-white" data-testid="stock-take-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowClerkOnly(v => !v)}
              className={showClerkOnly
                ? "bg-cyan-950 border-cyan-600 text-cyan-300 hover:bg-cyan-900"
                : "bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-700"}
              data-testid="toggle-clerk-only-filter"
              title="Show only rows where a clerk captured a count or note">
              <Filter className="h-4 w-4 mr-1" />
              {showClerkOnly ? `Clerk-captured only (${clerkCapturedCount})` : 'Show clerk-captured only'}
            </Button>
            <Button variant="outline" onClick={handleFillFromSystem}
              className="bg-slate-900 border-slate-700 text-white hover:bg-slate-700"
              data-testid="prefill-counts-btn">
              <Upload className="h-4 w-4 mr-1" /> Pre-fill from system
            </Button>
            <Button variant="outline" onClick={handleClear}
              className="bg-slate-900 border-slate-700 text-rose-300 hover:bg-slate-700">
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Counts table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base text-white">
            Capture counted quantities
            <span className="text-slate-400 text-xs font-normal ml-2">— {filtered.length} products shown · {collectedCounts.length} counted</span>
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setHideSystemQty(v => !v)}
            className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-700 text-xs"
            data-testid="toggle-blind-mode">
            {hideSystemQty ? '👁 Show System Qty' : '🙈 Hide (Blind Mode)'}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No products match filters.</div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-900 z-10">
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Product</TableHead>
                    <TableHead className="text-slate-400">Category</TableHead>
                    {!hideSystemQty && <TableHead className="text-slate-400 text-right">System Qty</TableHead>}
                    {isAdmin && <TableHead className="text-slate-400 text-right w-28">Cost/Unit</TableHead>}
                    {isAdmin && <TableHead className="text-slate-400 text-right w-28">Sale/Unit</TableHead>}
                    {isAdmin && <TableHead className="text-cyan-400 text-right w-32">Cost Value</TableHead>}
                    {isAdmin && <TableHead className="text-emerald-400 text-right w-32">Sales Value</TableHead>}
                    <TableHead className="text-slate-400 text-right w-32">Counted Qty</TableHead>
                    {!hideSystemQty && <TableHead className="text-slate-400 text-right w-24">Diff</TableHead>}
                    <TableHead className="text-slate-400 w-72">
                      <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> Notes</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(p => {
                    const counted = counts[p.id];
                    const c = (counted === '' || counted === undefined) ? null : parseInt(counted) || 0;
                    const sys = parseInt(p.quantity || 0);
                    const diff = c == null ? null : c - sys;
                    const clerkCaptured = p.counted_by_role === 'clerk';
                    const anyCaptured = !!p.counted_by;
                    const rowHighlight = clerkCaptured
                      ? 'bg-cyan-950/30 hover:bg-cyan-900/40 border-l-2 border-l-cyan-500'
                      : anyCaptured
                        ? 'hover:bg-slate-800/40'
                        : 'hover:bg-slate-800/40';
                    return (
                      <TableRow key={p.id} className={`border-slate-700/60 ${rowHighlight}`} data-testid={`stock-row-${p.id}`}>
                        <TableCell className="text-white">
                          <div className="flex items-center gap-2">
                            <span>{p.name}</span>
                            {clerkCaptured && (
                              <Badge className="bg-cyan-950 border border-cyan-600 text-cyan-300 text-[10px] px-1.5 py-0" data-testid={`clerk-captured-badge-${p.id}`}>
                                <UserCog className="h-2.5 w-2.5 mr-1" /> Clerk
                              </Badge>
                            )}
                          </div>
                          {p.vehicle_source && <div className="text-slate-500 text-xs">{p.vehicle_source}</div>}
                        </TableCell>
                        <TableCell className="text-slate-300 text-sm">{p.category}</TableCell>
                        {!hideSystemQty && <TableCell className="text-right text-slate-300">{sys}</TableCell>}
                        {isAdmin && (
                          <TableCell className="text-right text-slate-400 text-xs font-mono" data-testid={`cost-unit-${p.id}`}>
                            {p.cost_price ? R(p.cost_price) : '—'}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-right text-slate-400 text-xs font-mono" data-testid={`sale-unit-${p.id}`}>
                            {(() => {
                              const bd = priceMap[p.id];
                              const sale = bd?.last_sold_price || p.selling_price || 0;
                              return sale ? (
                                <>
                                  {R(sale)}
                                  {bd?.source && (
                                    <div className="text-[9px] text-slate-500">{bd.source === 'history' ? 'sale hist' : bd.source === 'parts_catalog' ? 'parts cat' : bd.source === 'catalog' ? 'catalog' : 'at cost'}</div>
                                  )}
                                </>
                              ) : '—';
                            })()}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-right text-cyan-300 font-mono" data-testid={`cost-value-${p.id}`}>
                            {R(sys * (p.cost_price || 0))}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-right text-emerald-300 font-mono font-semibold" data-testid={`sales-value-${p.id}`}>
                            {R(priceMap[p.id]?.value ?? (sys * (p.selling_price || 0)))}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <Input type="number" min="0" value={counted ?? ''}
                            onChange={(e) => setCount(p.id, e.target.value)}
                            onBlur={(e) => persistCount(p, e.target.value)}
                            placeholder="—"
                            className={`bg-slate-900 text-white text-right h-8 w-24 ml-auto ${anyCaptured ? 'border-cyan-600' : 'border-slate-700'}`}
                            data-testid={`count-input-${p.id}`} />
                          {p.counted_by && p.counted_at && (
                            <div className="text-[10px] text-cyan-400/80 mt-0.5" data-testid={`count-meta-${p.id}`}>
                              by {p.counted_by} · {new Date(p.counted_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                            </div>
                          )}
                        </TableCell>
                        {!hideSystemQty && (
                          <TableCell className="text-right">
                            {diff == null ? <span className="text-slate-600">—</span> :
                              diff === 0 ? <span className="text-emerald-400 font-semibold">0</span> :
                              diff < 0 ? <span className="text-rose-400 font-semibold">{diff}</span> :
                              <span className="text-amber-400 font-semibold">+{diff}</span>}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-start gap-1">
                            <Textarea
                              value={noteDraftValue(p)}
                              onChange={(e) => handleNoteChange(p.id, e.target.value)}
                              placeholder="Add note (e.g. damaged, bay 3, short-shipped)"
                              rows={1}
                              className="bg-slate-900 border-slate-700 text-white text-xs min-h-[32px] resize-y py-1 px-2"
                              data-testid={`stock-note-input-${p.id}`}
                            />
                            <Button
                              size="sm"
                              disabled={!noteChanged(p) || savingNote[p.id]}
                              onClick={() => handleNoteSave(p)}
                              className="h-8 px-2 bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-40"
                              data-testid={`stock-note-save-${p.id}`}
                              title="Save note"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          {p.stock_notes_by && p.stock_notes_at && (
                            <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                              by {p.stock_notes_by} · {new Date(p.stock_notes_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {isAdmin && (
                    <TableRow className="border-slate-700 bg-slate-900 sticky bottom-0 font-bold">
                      <TableCell className="text-white">TOTALS ({filtered.length})</TableCell>
                      <TableCell />
                      {!hideSystemQty && (
                        <TableCell className="text-right text-slate-300" data-testid="stock-take-total-qty">
                          {filtered.reduce((s, p) => s + parseInt(p.quantity || 0), 0)}
                        </TableCell>
                      )}
                      <TableCell /><TableCell />
                      <TableCell className="text-right text-cyan-300 font-mono" data-testid="stock-take-total-cost-value">
                        {R(filtered.reduce((s, p) => s + parseInt(p.quantity || 0) * (p.cost_price || 0), 0))}
                      </TableCell>
                      <TableCell className="text-right text-emerald-300 font-mono" data-testid="stock-take-total-sales-value">
                        {R(filtered.reduce((s, p) => s + (priceMap[p.id]?.value ?? (parseInt(p.quantity || 0) * (p.selling_price || 0))), 0))}
                      </TableCell>
                      <TableCell /><TableCell />
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview / Apply */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handlePreview} disabled={previewing || collectedCounts.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="preview-variance-btn">
              <Calculator className="h-4 w-4 mr-1" /> {previewing ? 'Computing...' : 'Preview Variance'}
            </Button>
            <Button onClick={handleApply} disabled={applying || !preview || collectedCounts.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="apply-stock-take-btn">
              <Save className="h-4 w-4 mr-1" /> {applying ? 'Applying...' : 'Apply Stock Take'}
            </Button>
            <span className="text-slate-400 text-xs">{collectedCounts.length} products with counted quantities</span>
          </div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={2} placeholder="Optional notes about this stock take (e.g. 'End of April count by Sipho & LEETON')"
            className="bg-slate-900 border-slate-700 text-white"
            data-testid="stock-take-notes" />

          {preview && (
            <div className="space-y-3" data-testid="preview-results">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div className="bg-slate-900 rounded p-3 border border-slate-700">
                  <div className="text-[10px] text-slate-500 uppercase">Items</div>
                  <div className="text-xl font-bold text-white">{preview.summary.total_items}</div>
                </div>
                <div className="bg-slate-900 rounded p-3 border border-emerald-700/50">
                  <div className="text-[10px] text-emerald-400 uppercase">Matches</div>
                  <div className="text-xl font-bold text-emerald-400">{preview.summary.matches}</div>
                </div>
                <div className="bg-slate-900 rounded p-3 border border-rose-700/50">
                  <div className="text-[10px] text-rose-400 uppercase">Shortages</div>
                  <div className="text-xl font-bold text-rose-400">{preview.summary.shortages}</div>
                  <div className="text-[10px] text-rose-400/70">−{R(preview.summary.shortage_value)}</div>
                </div>
                <div className="bg-slate-900 rounded p-3 border border-amber-700/50">
                  <div className="text-[10px] text-amber-400 uppercase">Overages</div>
                  <div className="text-xl font-bold text-amber-400">{preview.summary.overages}</div>
                  <div className="text-[10px] text-amber-400/70">+{R(preview.summary.overage_value)}</div>
                </div>
                <div className="bg-slate-900 rounded p-3 border border-slate-700">
                  <div className="text-[10px] text-slate-500 uppercase">Net value</div>
                  <div className={`text-xl font-bold ${(preview.summary.overage_value - preview.summary.shortage_value) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {R(preview.summary.overage_value - preview.summary.shortage_value)}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded border border-slate-700 max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-900">
                    <TableRow className="border-slate-700 hover:bg-transparent">
                      <TableHead className="text-slate-400">Product</TableHead>
                      <TableHead className="text-slate-400 text-right">System</TableHead>
                      <TableHead className="text-slate-400 text-right">Counted</TableHead>
                      <TableHead className="text-slate-400 text-right">Diff</TableHead>
                      <TableHead className="text-slate-400 text-right">Value Impact</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.filter(r => r.difference !== 0).map(r => (
                      <TableRow key={r.product_id} className="border-slate-800 hover:bg-slate-800/40">
                        <TableCell className="text-white text-sm">
                          {r.name}
                          {r.vehicle_source && <span className="text-slate-500 text-xs block">{r.vehicle_source}</span>}
                        </TableCell>
                        <TableCell className="text-right text-slate-300">{r.system_qty}</TableCell>
                        <TableCell className="text-right text-slate-300">{r.counted_qty}</TableCell>
                        <TableCell className={`text-right font-semibold ${r.difference < 0 ? 'text-rose-400' : 'text-amber-400'}`}>
                          {r.difference > 0 ? '+' : ''}{r.difference}
                        </TableCell>
                        <TableCell className={`text-right ${r.value_diff < 0 ? 'text-rose-400' : 'text-amber-400'}`}>
                          {r.value_diff > 0 ? '+' : ''}{R(r.value_diff)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {preview.rows.filter(r => r.difference !== 0).length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-emerald-400 py-4">All counts match the system. </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="stock-take-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HistoryIcon className="h-5 w-5 text-cyan-400" /> Stock Take History
            </DialogTitle>
          </DialogHeader>
          {history.length === 0 ? (
            <div className="text-slate-400 text-sm py-6 text-center">No stock takes recorded yet.</div>
          ) : (
            <div className="space-y-3">
              {history.map(h => (
                <div key={h.id} className="bg-slate-800/60 border border-slate-700 rounded p-3">
                  <div className="flex justify-between text-sm">
                    <div>
                      <span className="text-white font-semibold">{new Date(h.applied_at).toLocaleString('en-ZA')}</span>
                      <span className="text-slate-400 text-xs ml-2">by {h.applied_by}</span>
                    </div>
                    <Badge className="bg-emerald-950 border border-emerald-700 text-emerald-300">
                      {h.rows_updated} updated · {h.rows_skipped} skipped
                    </Badge>
                  </div>
                  {h.notes && <p className="text-slate-300 text-xs italic mt-1">{h.notes}</p>}
                  <div className="text-xs text-slate-400 mt-2">
                    Changes: {h.rows.filter(r => r.difference !== 0).length} items had variance
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setHistoryOpen(false)} className="bg-slate-700 hover:bg-slate-600 text-white">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>}
    </div>
  );
}
